import { useState, useEffect, useRef, useCallback } from 'react';
import ImgCrop from 'antd-img-crop';
import { Upload, Image, Button, Typography, Space } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import type { ImageKind } from '@/api/generated/schemas';
import { ImageUploadInContentType } from '@/api/generated/schemas';
import { useSignedUpload } from '@/hooks/useSignedUpload';
import type { RcFile } from 'antd/es/upload';

const ALLOWED_MIME_TYPES = Object.values(ImageUploadInContentType);
const MAX_FILE_SIZE_MB = 10;

type Status = 'idle' | 'uploading' | 'success' | 'error' | 'removed';

interface ImageUploadFieldProps {
  imageKind: ImageKind;
  currentUrl?: string | null;
  /** Called with the new storage key, or `null` when the image is removed. */
  onUpload: (imageKey: string | null) => void;
  label?: string;
  /** Shows the required mark next to the label; validation stays on the form. */
  required?: boolean;
  disabled?: boolean;
  aspect?: number;
}

export const ImageUploadField = ({
  imageKind,
  currentUrl,
  onUpload,
  label,
  required = false,
  disabled = false,
  aspect,
}: ImageUploadFieldProps) => {
  const t = useTranslate();
  const { upload } = useSignedUpload();
  const [localPreviewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  // `currentUrl` still holds the saved image after a removal, so the flag has to
  // outrank it until the form is submitted.
  const [removed, setRemoved] = useState(false);
  const previewUrl = removed ? null : (localPreviewUrl ?? currentUrl ?? null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const beforeCrop = useCallback(
    (file: RcFile): boolean => {
      if (!ALLOWED_MIME_TYPES.includes(file.type as ImageUploadInContentType)) {
        setStatus('error');
        setErrorMessage(t('base.upload.invalid_type'));
        return false;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setStatus('error');
        setErrorMessage(
          t('base.upload.file_too_large', { size: MAX_FILE_SIZE_MB })
        );
        return false;
      }
      setStatus('idle');
      setErrorMessage(null);
      return true;
    },
    [t]
  );

  const handleCroppedFile = useCallback(
    async (croppedFile: RcFile | File): Promise<false> => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const localUrl = URL.createObjectURL(croppedFile);
      objectUrlRef.current = localUrl;
      setPreviewUrl(localUrl);
      setRemoved(false);
      setErrorMessage(null);

      try {
        setStatus('uploading');
        const { imageKey, publicUrl } = await upload(
          croppedFile as File,
          imageKind
        );

        setPreviewUrl(publicUrl);
        setStatus('success');
        onUpload(imageKey);
      } catch (err) {
        setStatus('error');
        setErrorMessage(
          err instanceof Error ? err.message : t('base.upload.upload_error')
        );
      }

      return false;
    },
    [imageKind, upload, onUpload, t]
  );

  const handleRemove = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);
    setRemoved(true);
    setErrorMessage(null);
    setStatus('removed');
    onUpload(null);
  }, [onUpload]);

  const busy = status === 'uploading';

  const buttonLabel = busy
    ? t('base.upload.uploading')
    : t('base.upload.select_file');

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {label && (
        <Typography.Text strong>
          {required && (
            <Typography.Text type="danger" style={{ marginInlineEnd: 4 }}>
              *
            </Typography.Text>
          )}
          {label}
        </Typography.Text>
      )}

      {previewUrl && (
        <Image
          src={previewUrl}
          height={120}
          style={{ objectFit: 'cover', display: 'block' }}
          preview={{ mask: t('base.upload.preview') }}
        />
      )}

      <ImgCrop
        rotationSlider
        aspect={aspect}
        aspectSlider={aspect === undefined}
        zoomSlider
        showGrid
        showReset
        quality={1}
        fillColor="transparent"
        beforeCrop={beforeCrop}
        modalTitle={t('base.upload.crop_modal_title')}
        modalOk={t('base.upload.crop_ok')}
        modalCancel={t('base.upload.crop_cancel')}
      >
        <Upload
          accept={ALLOWED_MIME_TYPES.join(',')}
          showUploadList={false}
          beforeUpload={handleCroppedFile}
          disabled={disabled || busy}
        >
          <Button icon={<UploadOutlined />} loading={busy} disabled={disabled}>
            {buttonLabel}
          </Button>
        </Upload>
      </ImgCrop>

      {previewUrl && !disabled && (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          disabled={busy}
          onClick={handleRemove}
        >
          {t('base.upload.remove')}
        </Button>
      )}

      {status === 'success' && (
        <Typography.Text type="success">
          {t('base.upload.upload_success')}
        </Typography.Text>
      )}
      {status === 'removed' && (
        <Typography.Text type="secondary">
          {t('base.upload.remove_success')}
        </Typography.Text>
      )}
      {status === 'error' && errorMessage && (
        <Typography.Text type="danger">{errorMessage}</Typography.Text>
      )}
    </Space>
  );
};
